import { DataInstance } from "@busy-human/gearbox";
import { ManagementTrainers } from "@busy-human/k9-library";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { useAuth } from "./auth";
import { adminCheckEmail } from "@/util/functions";

export const useTrainers = defineStore("trainers", () => {
    const trainers = ref(new Map<string, DataInstance<ManagementTrainers.Model>>());
    const auth = useAuth();
    const unsub = ref<(() => void) | null>(null);
    const loaded = ref(false);

    watch(() => auth.currentUid, async (uid) => {
        loaded.value = false;
        if(uid) {
            const col = ManagementTrainers.Collection.all();
            col.onUpdate(() => {
                trainers.value.clear();
                col.dataItems().forEach(t => {
                    trainers.value.set(t.$id, t);
                });
            });
            await col.listen();
            unsub.value = () => col.cleanup();
        } else {
            if(unsub.value) unsub.value();
            trainers.value.clear();
        }
        loaded.value = true;
    }, { immediate: true });

    async function addTrainer(trainer: ManagementTrainers.Model) {
        let {userId} = trainer;
        if(!userId) {
            const { data: res } = await adminCheckEmail({ email: trainer.email });
            if(res.exists) {
                userId = res.uid;
            } else {
                throw new Error("User with that email does not exist!");
            }
        }

        for(const t of trainers.value.values()) {
            if(t.userId === userId) {
                throw new Error("User with that email already exists");
            }
        }

        await ManagementTrainers.Collection.add({... trainer, userId}, {id: userId});
        return userId;
    }

    async function updateTrainer(id: string, trainer: Partial<ManagementTrainers.Model>) {
        if(trainer.userId !== id) trainer.userId = id;
        await ManagementTrainers.Collection.updateDoc(id, trainer);
    }

    /** NOTE: This only deletes the trainer record, it does not delete the user. */
    async function deleteTrainer(id: string) {
        await ManagementTrainers.Collection.deleteDoc(id);
    }

    return { trainers, loaded, addTrainer, updateTrainer, deleteTrainer };
});